import React, { useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  GraduationCap, 
  CheckCircle, 
  Sparkles, 
  User,
  BookOpen, 
  Award,
  Send,
  Lightbulb,
  Zap,
  Star,
  TrendingUp,
  Clock,
  Info,
  FileText,
  Upload,
  X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function ApplyAsTutor() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    education: "",
    currentSchool: "",
    graduationYear: "",
    experience: "",
    availability: "",
    motivation: "",
    achievements: ""
  });

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [focusedField, setFocusedField] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState("");
  const [errors, setErrors] = useState({});
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 15;
  };
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };
  const subjects = [
    "Math", "English", "History", "Biology", 
    "Chemistry", "Physics", "Computer Science", "Other"
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Flexible scheduling",
      description: "Work around your classes",
      color: "from-yellow-400 to-orange-500",
      bgColor: "bg-gradient-to-br from-yellow-50 to-orange-50",
      darkBgColor: "dark:bg-gradient-to-br dark:from-orange-950/70 dark:to-amber-950/60"
    },
    {
      icon: Award,
      title: "Competitive pay",
      description: "with flexible hours",
      color: "from-green-400 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-50 to-emerald-50",
      darkBgColor: "dark:bg-gradient-to-br dark:from-emerald-950/70 dark:to-teal-950/60"
    },
    {
      icon: Sparkles,
      title: "Make an impact",
      description: "Transform students' lives",
      color: "from-blue-400 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
      darkBgColor: "dark:bg-gradient-to-br dark:from-blue-950/70 dark:to-cyan-950/60"
    },
    {
      icon: TrendingUp,
      title: "Build experience",
      description: "Grow your resume",
      color: "from-purple-400 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
      darkBgColor: "dark:bg-gradient-to-br dark:from-purple-950/70 dark:to-pink-950/60"
    },
    {
      icon: Star,
      title: "Work remotely",
      description: "Tutor from anywhere",
      color: "from-rose-400 to-red-500",
      bgColor: "bg-gradient-to-br from-rose-50 to-red-50",
      darkBgColor: "dark:bg-gradient-to-br dark:from-rose-950/70 dark:to-red-950/60"
    },
    {
      icon: GraduationCap,
      title: "Supportive community",
      description: "Join amazing tutors",
      color: "from-indigo-400 to-blue-500",
      bgColor: "bg-gradient-to-br from-indigo-50 to-blue-50",
      darkBgColor: "dark:bg-gradient-to-br dark:from-indigo-950/70 dark:to-blue-950/60"
    }
  ];

  const requirements = [
    "Currently enrolled in college or recent graduate",
    "Strong academic record (3.5+ GPA preferred)",
    "Passion for teaching and helping others",
    "Excellent communication skills",
    "Reliable and professional attitude",
    "Available for at least 5 hours per week"
  ];

  const handleSubjectToggle = (subject) => {
    if (selectedSubjects.includes(subject)) {
      setSelectedSubjects(selectedSubjects.filter(s => s !== subject));
    } else {
      setSelectedSubjects([...selectedSubjects, subject]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let newValue = value;
    
    // Format phone numbers as user types
    if (name === 'phone') {
      newValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    
    // Validate email in real-time (optional, for better UX)
    if (name === 'email' && value.trim()) {
      if (!validateEmail(value.trim())) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: "Please enter a valid email address" 
        }));
      }
    }
    
    // Validate phone in real-time (optional, for better UX)
    if (name === 'phone' && value.trim()) {
      if (!validatePhone(value.replace(/\D/g, ''))) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: "Please enter a valid phone number (10-15 digits)" 
        }));
      }
    }
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setResumeError("Please upload a PDF or Word document");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setResumeError("File size must be less than 5MB");
      return;
    }

    setResumeFile(file);
    setResumeError("");
  };

  const removeResume = () => {
    setResumeFile(null);
    setResumeError("");
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number (10-15 digits)";
    }
    if (!formData.currentSchool.trim()) newErrors.currentSchool = "Current school is required";
    if (!formData.graduationYear.trim()) newErrors.graduationYear = "Graduation year is required";
    if (!formData.education.trim()) newErrors.education = "Education background is required";
    if (!formData.availability.trim()) newErrors.availability = "Availability is required";
    if (!formData.motivation.trim()) newErrors.motivation = "Motivation is required";
    if (selectedSubjects.length === 0) newErrors.subjects = "Please select at least one subject";
    if (!resumeFile) newErrors.resume = "Resume upload is required";
    
    // Set errors and return if any
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      alert("Please fix the errors in the form before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      // First upload the resume file
      let resumeUrl = "";
      if (resumeFile) {
        const resumeFormData = new FormData();
        resumeFormData.append('resume', resumeFile);
        
        console.log("Uploading resume...");
        const uploadResponse = await fetch('https://api.zenithacademics.com/api/upload/resume', {
          method: 'POST',
          body: resumeFormData
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload resume');
        }

        const uploadData = await uploadResponse.json();
        console.log("Resume upload response:", uploadData);
        
        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Resume upload failed');
        }

        resumeUrl = uploadData.fileUrl || "";
        console.log('Resume uploaded:', resumeUrl);
      }

      // Then submit the application with resume URL
      console.log("Sending data to:", 'https://api.zenithacademics.com/api/applications');
      console.log("Data being sent:", { 
        ...formData, 
        subjects: selectedSubjects,
        resumeUrl 
      });
      
      const response = await fetch('https://api.zenithacademics.com/api/applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          education: formData.education,
          currentSchool: formData.currentSchool,
          graduationYear: formData.graduationYear,
          subjects: selectedSubjects,
          experience: formData.experience || "",
          availability: formData.availability,
          motivation: formData.motivation,
          achievements: formData.achievements || "",
          resumeUrl: resumeUrl
        })
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);
      
      const data = await response.json();
      console.log("Server response:", data);

      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      alert(`Thank you for applying! Your application ID is ${data.applicationId}. We'll review your application and get back to you soon.`);
      
      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        education: "",
        currentSchool: "",
        graduationYear: "",
        experience: "",
        availability: "",
        motivation: "",
        achievements: ""
      });
      setSelectedSubjects([]);
      setResumeFile(null);

    } catch (error) {
      console.error('Error submitting application:', error);
      console.error('Error details:', error.message);
      alert(`Error: ${error.message}\n\nMake sure the backend server is running on port 5001.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const subjectColors = {
    Math: "from-blue-500 to-cyan-400",
    English: "from-rose-500 to-pink-400",
    History: "from-orange-500 to-amber-400",
    Biology: "from-emerald-500 to-lime-400",
    Chemistry: "from-pink-500 to-fuchsia-400",
    Physics: "from-indigo-500 to-sky-400",
    "Computer Science": "from-purple-500 to-violet-400",
    Other: "from-gray-500 to-slate-400"
  };

  const subjectColorsDark = {
    Math: "from-red-500 to-orange-500",
    English: "from-orange-500 to-amber-500",
    History: "from-red-500 to-orange-500",
    Biology: "from-orange-500 to-amber-500",
    Chemistry: "from-red-500 to-orange-500",
    Physics: "from-orange-500 to-amber-500",
    "Computer Science": "from-red-500 to-orange-500",
    Other: "from-zinc-600 to-zinc-500"
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <div className="min-h-screen py-12 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 relative overflow-hidden transition-colors duration-300">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20 dark:opacity-30">
        <motion.div 
          className="absolute top-20 left-10 w-96 h-96 bg-blue-300 dark:bg-red-600/30 rounded-full blur-3xl animate-pulse"
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-1/3 right-10 w-80 h-80 bg-purple-300 dark:bg-orange-500/30 rounded-full blur-3xl animate-pulse"
          animate={{ 
            scale: [1, 1.3, 1],
            x: [0, -30, 0],
            y: [0, 50, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div 
          className="absolute bottom-20 left-1/4 w-96 h-96 bg-pink-300 dark:bg-red-500/20 rounded-full blur-3xl animate-pulse"
          animate={{ 
            scale: [1, 1.1, 1],
            x: [0, 40, 0],
            y: [0, -40, 0]
          }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </div>

      {/* Floating shapes */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-32 right-20 hidden lg:block"
      >
        <div className="w-20 h-20 bg-yellow-300 dark:bg-amber-500/40 rounded-2xl opacity-30 dark:opacity-40" />
      </motion.div>

      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-40 left-20 hidden lg:block"
      >
        <div className="w-16 h-16 bg-blue-300 dark:bg-red-500/30 rounded-full opacity-30 dark:opacity-40" />
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1 
            className="text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-green-900 via-blue-900 to-purple-900 dark:bg-gradient-to-r dark:from-white dark:via-red-400 dark:to-orange-400 bg-clip-text text-transparent mb-6"
            animate={{ backgroundPosition: ["0%", "100%", "0%"] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            Become a Tutor
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 dark:text-zinc-300 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Share your knowledge, earn competitive pay, and <span className="font-bold text-blue-600 dark:text-red-400">transform students' lives</span> through peer-to-peer learning.
          </motion.p>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="text-center mb-8">
            <motion.h2 
              className="text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 dark:bg-gradient-to-r dark:from-red-400 dark:to-orange-400 bg-clip-text text-transparent mb-3"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              Why Join Us?
            </motion.h2>
            <p className="text-lg text-gray-600 dark:text-zinc-400">Discover the amazing benefits of being a Zenith tutor</p>
          </div>
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                whileHover={{ y: -12, rotate: [0, -2, 2, 0] }}
                transition={{ duration: 0.3 }}
              >
                <Card className={`p-6 border-3 border-transparent hover:border-purple-300 dark:hover:border-purple-600/50 transition-all duration-300 hover:shadow-2xl ${benefit.bgColor} ${benefit.darkBgColor} group relative overflow-hidden`}>
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-br from-white/50 dark:from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                    initial={false}
                  />
                  <motion.div 
                    className="absolute -right-10 -top-10 w-32 h-32 bg-gradient-to-br from-white/30 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                    transition={{ duration: 8, repeat: Infinity }}
                  />
                  <div className="relative">
                    <motion.div 
                      className={`w-14 h-14 bg-gradient-to-br ${benefit.color} dark:${benefit.color.replace('400', '500').replace('500', '600')} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <benefit.icon className="w-7 h-7 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-700 dark:text-zinc-300 font-medium">{benefit.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Requirements Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <Card className="p-8 md:p-10 border-3 border-blue-300 dark:border-red-500/40 bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:bg-gradient-to-br dark:from-zinc-900/90 dark:to-zinc-800/90 shadow-2xl relative overflow-hidden backdrop-blur-sm">
            <div className="relative">
              <div className="flex items-center gap-4 mb-8">
                <motion.div 
                  className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 dark:from-red-500 dark:to-orange-500 rounded-2xl flex items-center justify-center shadow-xl"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <CheckCircle className="w-7 h-7 text-white" />
                </motion.div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-cyan-600 dark:bg-gradient-to-r dark:from-red-400 dark:to-orange-400 bg-clip-text text-transparent">
                    Requirements
                  </h2>
                  <p className="text-gray-700 dark:text-zinc-300 mt-2 font-medium">What we're looking for in our tutors</p>
                </div>
              </div>
              <motion.div 
                className="grid md:grid-cols-2 gap-4"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {requirements.map((req, idx) => (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    className="flex items-start gap-3 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm p-5 rounded-xl border-2 border-blue-200 dark:border-red-500/30 shadow-md hover:border-blue-300 dark:hover:border-orange-500 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.div 
                      className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 dark:from-red-500 dark:to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <CheckCircle className="w-5 h-5 text-white" />
                    </motion.div>
                    <span className="text-gray-800 dark:text-zinc-200 font-semibold leading-relaxed">{req}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* Application Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-8 md:p-12 border-3 border-purple-300 dark:border-purple-600/50 bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 dark:bg-gradient-to-br dark:from-purple-950/70 dark:via-pink-950/60 dark:to-rose-950/60 shadow-2xl relative overflow-hidden backdrop-blur-sm">
            {/* Decorative animated corners */}
            <motion.div 
              className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-400 to-pink-400 dark:from-purple-700/30 dark:to-pink-700/30 opacity-20 rounded-bl-full"
              animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
              transition={{ duration: 10, repeat: Infinity }}
            />
            <motion.div 
              className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-blue-400 to-cyan-400 dark:from-blue-700/30 dark:to-cyan-700/30 opacity-20 rounded-tr-full"
              animate={{ scale: [1, 1.2, 1], rotate: [0, -90, 0] }}
              transition={{ duration: 10, repeat: Infinity }}
            />

            <div className="relative">
              <div className="text-center mb-10">
                <motion.div 
                  className="inline-flex items-center gap-3 mb-4"
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div 
                    className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 dark:from-red-500 dark:to-orange-600 rounded-2xl flex items-center justify-center shadow-xl"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Lightbulb className="w-7 h-7 text-white" />
                  </motion.div>
                  <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-purple-600 to-pink-600 dark:bg-gradient-to-r dark:from-red-400 dark:to-orange-400 bg-clip-text text-transparent">
                    Application Form
                  </h2>
                </motion.div>
                <p className="text-gray-700 dark:text-zinc-300 text-lg font-medium">Fill out the form below to start your journey as a tutor</p>
              </div>

              <motion.div 
                className="space-y-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Personal Information */}
                <motion.div 
                  className="space-y-6 p-6 bg-gradient-to-br from-blue-100 via-cyan-100 to-sky-100 dark:bg-gradient-to-br dark:from-blue-950/70 dark:via-cyan-950/60 dark:to-sky-950/60 rounded-2xl border-3 border-blue-300 dark:border-blue-600/50 shadow-lg relative overflow-hidden"
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                >
                  <motion.div 
                    className="absolute -right-20 -top-20 w-40 h-40 bg-gradient-to-br from-white/40 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.3, 1], rotate: [0, 180, 360] }}
                    transition={{ duration: 12, repeat: Infinity }}
                  />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3 relative">
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 dark:from-blue-600 dark:to-cyan-600 rounded-xl flex items-center justify-center shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <User className="w-5 h-5 text-white" />
                    </motion.div>
                    Personal Information
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6 relative">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      onFocus={() => setFocusedField('fullName')}
                      onBlur={() => setFocusedField(null)}
                    >
                      <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-3 border-blue-300 dark:border-blue-600/50 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                        placeholder="John Doe"
                      />
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                    >
                      <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-3 border-blue-300 dark:border-blue-600/50 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                        placeholder="john@example.com"
                        inputMode="email"
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-2 font-medium">{errors.email}</p>}
                    </motion.div>
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    className="relative"
                  >
                    <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength={15}
                      className="w-full px-4 py-3 border-3 border-blue-300 dark:border-blue-600/50 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-500/20 transition-all bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                      placeholder="(555) 123-4567"
                      pattern="[0-9\s\-\(\)]*"
                      inputMode="tel"
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-2 font-medium">{errors.phone}</p>}
                  </motion.div>
                </motion.div>

                {/* Resume Upload - NEW SECTION */}
                <motion.div 
                  className="space-y-6 p-6 bg-gradient-to-br from-indigo-100 via-violet-100 to-purple-100 dark:bg-gradient-to-br dark:from-indigo-950/70 dark:via-violet-950/60 dark:to-purple-950/60 rounded-2xl border-3 border-indigo-300 dark:border-purple-600/50 shadow-lg relative overflow-hidden"
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                >
                  <motion.div 
                    className="absolute -left-20 top-1/2 w-40 h-40 bg-gradient-to-br from-white/40 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.3, 1], rotate: [0, -90, -180] }}
                    transition={{ duration: 12, repeat: Infinity }}
                  />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3 relative">
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 dark:from-purple-600 dark:to-violet-600 rounded-xl flex items-center justify-center shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <FileText className="w-5 h-5 text-white" />
                    </motion.div>
                    Resume Upload *
                  </h3>

                  <motion.div className="space-y-4 relative">
                    <div className="text-left">
                      <p className="text-gray-700 dark:text-zinc-300 mb-4 font-medium">
                        Please upload your resume in PDF or Word format (max 5MB)
                      </p>
                      
                      {!resumeFile ? (
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <input
                            type="file"
                            onChange={handleResumeUpload}
                            accept=".pdf,.doc,.docx"
                            className="hidden"
                            id="resume-upload"
                          />
                          <label
                            htmlFor="resume-upload"
                            className={`flex flex-col items-center justify-center w-full h-48 border-3 ${resumeError ? 'border-red-300 dark:border-red-500/50 bg-red-50/50 dark:bg-red-950/30' : 'border-indigo-300 dark:border-purple-600/50 hover:border-indigo-400 dark:hover:border-purple-500'} border-dashed rounded-2xl cursor-pointer transition-all duration-300 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm shadow-md`}
                          >
                            <motion.div
                              animate={{ y: [0, -5, 0] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              <Upload className={`w-16 h-16 mb-4 ${resumeError ? 'text-red-400 dark:text-red-400' : 'text-indigo-400 dark:text-purple-400'}`} />
                            </motion.div>
                            <p className={`text-lg font-bold ${resumeError ? 'text-red-600 dark:text-red-300' : 'text-gray-700 dark:text-zinc-300'} mb-2`}>
                              Click to upload your resume
                            </p>
                            <p className={`text-sm ${resumeError ? 'text-red-500 dark:text-red-400' : 'text-gray-600 dark:text-zinc-400'}`}>
                              PDF or Word document (max 5MB)
                            </p>
                          </label>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-center justify-between p-6 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 rounded-2xl border-3 border-emerald-300 dark:border-emerald-500/50 shadow-lg"
                        >
                          <div className="flex items-center gap-4">
                            <motion.div 
                              className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-500 dark:from-emerald-600 dark:to-green-600 rounded-xl flex items-center justify-center shadow-md"
                              whileHover={{ rotate: 360 }}
                              transition={{ duration: 0.5 }}
                            >
                              <FileText className="w-7 h-7 text-white" />
                            </motion.div>
                            <div>
                              <h4 className="font-bold text-gray-900 dark:text-white text-lg">{resumeFile.name}</h4>
                              <p className="text-sm text-gray-600 dark:text-zinc-400">
                                {(resumeFile.size / 1024 / 1024).toFixed(2)} MB • {resumeFile.type === 'application/pdf' ? 'PDF' : 'Word Document'}
                              </p>
                              <motion.p 
                                className="text-sm text-emerald-600 dark:text-emerald-400 font-medium mt-1 flex items-center gap-1"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Resume uploaded successfully
                              </motion.p>
                            </div>
                          </div>
                          <motion.button
                            type="button"
                            onClick={removeResume}
                            className="p-3 rounded-xl bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30 hover:from-red-100 hover:to-rose-100 dark:hover:from-red-900/50 dark:hover:to-rose-900/50 border-2 border-red-200 dark:border-red-500/30 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <X className="w-5 h-5 text-red-500 dark:text-red-400" />
                          </motion.button>
                        </motion.div>
                      )}
                      
                      {resumeError && (
                        <motion.p 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-500 dark:text-red-400 text-sm font-medium mt-3 flex items-center gap-2"
                        >
                          <Info className="w-4 h-4" />
                          {resumeError}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>
                </motion.div>

                {/* Education */}
                <motion.div 
                  className="space-y-6 p-6 bg-gradient-to-br from-purple-100 via-pink-100 to-fuchsia-100 dark:bg-gradient-to-br dark:from-purple-950/70 dark:via-pink-950/60 dark:to-fuchsia-950/60 rounded-2xl border-3 border-purple-300 dark:border-purple-600/50 shadow-lg relative overflow-hidden"
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                >
                  <motion.div 
                    className="absolute -left-20 -bottom-20 w-40 h-40 bg-gradient-to-br from-white/40 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.3, 1], rotate: [0, -180, -360] }}
                    transition={{ duration: 12, repeat: Infinity }}
                  />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3 relative">
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 dark:from-purple-600 dark:to-pink-600 rounded-xl flex items-center justify-center shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <GraduationCap className="w-5 h-5 text-white" />
                    </motion.div>
                    Education
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6 relative">
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                        Current School/University *
                      </label>
                      <input
                        type="text"
                        name="currentSchool"
                        value={formData.currentSchool}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-3 border-purple-300 dark:border-purple-600/50 rounded-xl focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-200 dark:focus:ring-purple-500/20 transition-all bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                        placeholder="Harvard University"
                      />
                    </motion.div>

                    <motion.div whileHover={{ scale: 1.02 }}>
                      <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                        Expected Graduation Year *
                      </label>
                      <input
                        type="text"
                        name="graduationYear"
                        value={formData.graduationYear}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-3 border-purple-300 dark:border-purple-600/50 rounded-xl focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-200 dark:focus:ring-purple-500/20 transition-all bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                        placeholder="2026"
                      />
                    </motion.div>
                  </div>

                  <motion.div whileHover={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                      Education Background *
                    </label>
                    <textarea
                      name="education"
                      value={formData.education}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-3 border-3 border-purple-300 dark:border-purple-600/50 rounded-xl focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-200 dark:focus:ring-purple-500/20 transition-all resize-none bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                      placeholder="List your high school, college, major, GPA, relevant coursework..."
                    />
                  </motion.div>
                </motion.div>

                {/* Subjects */}
                <motion.div 
                  className="space-y-6 p-6 bg-gradient-to-br from-green-100 via-emerald-100 to-teal-100 dark:bg-gradient-to-br dark:from-emerald-950/70 dark:via-teal-950/60 dark:to-cyan-950/60 rounded-2xl border-2 border-green-200 dark:border-emerald-600/50 shadow-lg relative overflow-hidden"
                  variants={itemVariants}
                >
                  <motion.div 
                    className="absolute -right-20 top-1/2 w-40 h-40 bg-gradient-to-br from-white/40 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.3, 1], y: [-20, 20, -20] }}
                    transition={{ duration: 8, repeat: Infinity }}
                  />
                  
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3 relative">
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 dark:from-emerald-600 dark:to-emerald-700 rounded-xl flex items-center justify-center shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <BookOpen className="w-5 h-5 text-white" />
                    </motion.div>
                    Subjects You Can Tutor *
                  </h3>

                  <div className="flex flex-wrap gap-3 relative">
                    <AnimatePresence>
                      {subjects.map((subject, idx) => (
                        <motion.button
                          key={subject}
                          type="button"
                          onClick={() => handleSubjectToggle(subject)}
                          className={`px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                            selectedSubjects.includes(subject)
                              ? `bg-gradient-to-r ${subjectColors[subject]} dark:${subjectColorsDark[subject]} text-white shadow-md border-transparent`
                              : "bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800 border border-gray-300 dark:border-emerald-600/50 shadow-sm"
                          }`}
                          whileHover={selectedSubjects.includes(subject) ? {} : { scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ 
                            duration: 0.2,
                            delay: idx * 0.03,
                            scale: { type: "spring", stiffness: 300 }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {selectedSubjects.includes(subject) && (
                              <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: "spring", stiffness: 200 }}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </motion.div>
                            )}
                            <span>{subject}</span>
                          </div>
                        </motion.button>
                      ))}
                    </AnimatePresence>
                  </div>
                  
                  <motion.p 
                    className="text-sm text-gray-600 dark:text-zinc-400 mt-4 flex items-center gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Info className="w-4 h-4" />
                    Select all subjects you're comfortable tutoring
                  </motion.p>
                </motion.div>

                {/* Experience & Availability */}
                <motion.div 
                  className="space-y-6 p-6 bg-gradient-to-br from-orange-100 via-amber-100 to-yellow-100 dark:bg-gradient-to-br dark:from-orange-950/70 dark:via-amber-950/60 dark:to-yellow-950/60 rounded-2xl border-3 border-orange-300 dark:border-orange-600/50 shadow-lg relative overflow-hidden"
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                >
                  <motion.div 
                    className="absolute -left-20 top-10 w-40 h-40 bg-gradient-to-br from-white/40 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.3, 1], x: [0, 20, 0] }}
                    transition={{ duration: 10, repeat: Infinity }}
                  />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3 relative">
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 dark:from-orange-600 dark:to-amber-600 rounded-xl flex items-center justify-center shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Clock className="w-5 h-5 text-white" />
                    </motion.div>
                    Experience & Availability
                  </h3>

                  <motion.div whileHover={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                      Teaching/Tutoring Experience
                    </label>
                    <textarea
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-3 border-3 border-orange-300 dark:border-orange-600/50 rounded-xl focus:border-orange-500 dark:focus:border-orange-400 focus:ring-4 focus:ring-orange-200 dark:focus:ring-orange-500/20 transition-all resize-none bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                      placeholder="Describe any previous tutoring, teaching, or mentoring experience..."
                    />
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                      Weekly Availability *
                    </label>
                    <textarea
                      name="availability"
                      value={formData.availability}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-3 border-3 border-orange-300 dark:border-orange-600/50 rounded-xl focus:border-orange-500 dark:focus:border-orange-400 focus:ring-4 focus:ring-orange-200 dark:focus:ring-orange-500/20 transition-all resize-none bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                      placeholder="e.g., Mondays 4-8pm, Tuesdays 5-9pm, Weekends flexible..."
                    />
                  </motion.div>
                </motion.div>

                {/* Additional Information */}
                <motion.div 
                  className="space-y-6 p-6 bg-gradient-to-br from-pink-100 via-rose-100 to-red-100 dark:bg-gradient-to-br dark:from-rose-950/70 dark:via-pink-950/60 dark:to-red-950/60 rounded-2xl border-3 border-pink-300 dark:border-rose-600/50 shadow-lg relative overflow-hidden"
                  variants={itemVariants}
                  whileHover={{ scale: 1.01 }}
                >
                  <motion.div 
                    className="absolute -right-20 -bottom-20 w-40 h-40 bg-gradient-to-br from-white/40 dark:from-white/10 to-transparent rounded-full"
                    animate={{ scale: [1, 1.3, 1], rotate: [0, 90, 180] }}
                    transition={{ duration: 15, repeat: Infinity }}
                  />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3 relative">
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 dark:from-rose-600 dark:to-pink-600 rounded-xl flex items-center justify-center shadow-md"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Sparkles className="w-5 h-5 text-white" />
                    </motion.div>
                    Additional Information
                  </h3>

                  <motion.div whileHover={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                      Why do you want to become a tutor? *
                    </label>
                    <textarea
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-3 border-3 border-pink-300 dark:border-rose-600/50 rounded-xl focus:border-pink-500 dark:focus:border-rose-400 focus:ring-4 focus:ring-pink-200 dark:focus:ring-rose-500/20 transition-all resize-none bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                      placeholder="Share your passion for teaching and helping students succeed..."
                    />
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.02 }} className="relative">
                    <label className="block text-sm font-bold text-gray-700 dark:text-zinc-300 mb-2">
                      Academic Achievements & Relevant Skills
                    </label>
                    <textarea
                      name="achievements"
                      value={formData.achievements}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-3 border-3 border-pink-300 dark:border-rose-600/50 rounded-xl focus:border-pink-500 dark:focus:border-rose-400 focus:ring-4 focus:ring-pink-200 dark:focus:ring-rose-500/20 transition-all resize-none bg-white dark:bg-zinc-900/80 shadow-md text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-zinc-500"
                      placeholder="List any awards, honors, test scores, or relevant skills..."
                    />
                  </motion.div>
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  className="pt-6"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:bg-gradient-to-r dark:from-red-500 dark:via-orange-500 dark:to-amber-500 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 dark:hover:from-red-600 dark:hover:via-orange-600 dark:hover:to-amber-600 text-white font-bold py-6 text-xl rounded-xl shadow-2xl relative overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 via-pink-400/30 to-purple-400/30 dark:from-red-400/30 dark:via-orange-400/30 dark:to-amber-400/30"
                      animate={{ 
                        x: ['-100%', '100%']
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                    <span className="relative z-10 flex items-center justify-center">
                      {isSubmitting ? (
                        <>
                          <motion.div
                            className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin mr-3"
                          />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <motion.div
                            animate={{ x: [0, 5, 0] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <Send className="w-6 h-6 ml-3" />
                          </motion.div>
                        </>
                      )}
                    </span>
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}